/**
 * Defines the PortfolioDetails component, a comprehensive dialog that presents detailed information about a portfolio,
 * including options to apply filters and perform actions such as inviting managers or adding portfolios.
 * The component is integrated with the application's context for state management and uses Auth0 for authentication checks.
 *
 * @module PortfolioDetails
 */
import {
    Dialog,
    DialogContent,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    MenuList,
    styled,
    Typography
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import React, { useState } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import CloseIcon from '@mui/icons-material/Close';
import { Stack } from '@mui/system';
import PropTypes from 'prop-types';
import { useAuth0 } from '@auth0/auth0-react';
import { userActionItems } from './menuItemsConfig';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(0),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3)
}));

const PortfolioDetailsStack = styled(Stack)(({ theme }) => ({
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3)
}));

const StyledIcon = styled('div')(() => ({
    transform: 'scale(1.2)'
}));

const StyledTypography = styled(Typography)(() => ({
    lineHeight: 1
}));

/**
 * PortfolioDetailsMenuItem renders an individual item in the Portfolio Details dialog.
 * Each item can trigger its own dialog and is optionally disabled based on the user's authentication state.
 *
 * @component
 * @param {Object} props - The properties passed to the component
 * @param {Object} props.itemConfig - Configuration for the item, including its title and dialog component
 * @param {boolean} props.disabled - Indicates whether the item is disabled
 * @param {Function} props.closePortfolioDetailsDialog - Function to close the Portfolio Details dialog
 * @returns {JSX.Element} A menu item with a dialog trigger
 */
const PortfolioDetailsMenuItem = ({
    itemConfig,
    disabled,
    closePortfolioDetailsDialog
}) => {
    const [dialogOpen, setDialogOpen] = useState(false);

    const handleDialogOpen = () => {
        setDialogOpen(true);
    };

    function handleDialogClose() {
        setDialogOpen(false);
    }

    /**
     * Form Submission View Close button required to also close
     * Portfolio Details dialog, in addition to the dialog in question.
     */
    function handleDialogSubmissionViewClose() {
        setDialogOpen(false);
        closePortfolioDetailsDialog();
    }

    return (
        <>
            <StyledMenuItem
                dense
                onClick={handleDialogOpen}
                disabled={disabled}
            >
                <ListItemIcon>
                    <StyledIcon as={itemConfig.IconComponent.outline} />
                </ListItemIcon>
                <ListItemText>
                    <StyledTypography>{itemConfig.title}</StyledTypography>
                </ListItemText>
            </StyledMenuItem>
            <itemConfig.DialogComponent
                open={dialogOpen}
                handleFormClose={handleDialogClose}
                handleSubmissionViewClose={handleDialogSubmissionViewClose}
            />
        </>
    );
};

PortfolioDetailsMenuItem.propTypes = {
    itemConfig: PropTypes.object.isRequired,
    disabled: PropTypes.bool.isRequired,
    closePortfolioDetailsDialog: PropTypes.func.isRequired
};

/**
 * PortfolioDetails presents a dialog full of detailed information about a portfolio,
 * including custom actions based on user roles and authentication state.
 *
 * @component
 * @param {Object} props - The properties passed to the component
 * @param {boolean} props.open - Controls the open state of the dialog
 * @param {Function} props.handleClose - A function to close the dialog
 * @param {Object} props.portfolioData - Data about the portfolio to be displayed
 * @returns {JSX.Element} A dialog containing detailed portfolio information and actions
 */
export const PortfolioDetails = ({
    portfolioData,
    open,
    handleClose,
    onVsPeerGroupClick,
    onMoreDetailsClick,
    onMoreDetailsDisabled = false
}) => {
    const { isAuthenticated } = useAuth0();

    return (
        <Dialog open={open} onClose={handleClose} fullScreen>
            <DialogTitle
                variant='header2'
                align={'left'}
                sx={{
                    display: 'flex',
                    alignItems: 'center'
                }}
            >
                Portfolio Details
                <IconButton sx={{ marginLeft: 'auto' }} onClick={handleClose}>
                    <StyledIcon as={CloseIcon} />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    padding: 0
                }}
            >
                <PortfolioDetailsStack>
                    <Typography variant='body1' fontWeight={500}>
                        {portfolioData?.portfolioLongName}
                    </Typography>
                    <Typography variant='body1' fontWeight={500}>
                        Behavioral Alpha Score |{' '}
                        {portfolioData?.baScore?.toFixed(2)}
                    </Typography>
                </PortfolioDetailsStack>
                <Divider variant='fullWidth' />

                <MenuList>
                    <StyledMenuItem dense onClick={onVsPeerGroupClick}>
                        <ListItemIcon>
                            <StyledIcon as={PeopleIcon} />
                        </ListItemIcon>
                        <ListItemText>
                            <StyledTypography>Vs. Peer Group</StyledTypography>
                        </ListItemText>
                    </StyledMenuItem>
                    <StyledMenuItem
                        dense
                        onClick={
                            onMoreDetailsDisabled ? null : onMoreDetailsClick
                        }
                        disabled={!isAuthenticated || onMoreDetailsDisabled}
                    >
                        <ListItemIcon>
                            <BarChartRoundedIcon
                                sx={{ transform: 'rotate(90deg) scale(1.3)' }}
                            />
                        </ListItemIcon>
                        <ListItemText>
                            <StyledTypography>More Details</StyledTypography>
                        </ListItemText>
                    </StyledMenuItem>
                    <Divider variant='fullWidth' />
                    {userActionItems.map((item) => (
                        <PortfolioDetailsMenuItem
                            key={item.id}
                            itemConfig={item}
                            disabled={!isAuthenticated}
                            closePortfolioDetailsDialog={handleClose}
                        />
                    ))}
                </MenuList>
            </DialogContent>
        </Dialog>
    );
};

PortfolioDetails.propTypes = {
    portfolioData: PropTypes.object,
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func,
    onVsPeerGroupClick: PropTypes.func,
    onMoreDetailsClick: PropTypes.func,
    onMoreDetailsDisabled: PropTypes.bool
};
